import { createContext, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Badge, Breadcrumbs, Button, Card, Pagination, Popover, RoundedTag, Select } from '@dreamtree-org/twreact-ui';
import { ChevronDown, SlidersHorizontal } from 'lucide-react';
import { EmptyState } from '@/components';
import { PAGE_SIZE, SORTS, activeChips, activeFilterCount, clearFilters, emptyFacets, facetsFrom, removeChip, setFilter, sortListings } from '../lib/filters';
import { collectionOf, collectionsOf } from '../lib/collections';
import { shopHref } from '../lib/shop';
import { pageWindow, resultsGrid } from '../lib/design';
import { useStorefront } from '../StorefrontContext';
import { shownText } from '../lib/placeholders';
import ListingCard from '../components/ListingCard';
import FilterPanel, { filterGroups } from '../components/FilterPanel';
import CatalogGate, { SkeletonGrid } from '../components/CatalogGate';

/**
 * The listing page's commerce slots (landing-kit LISTING_SLOTS). The page
 * (pages/Search.jsx) works out the results once and shares them here; each
 * slot draws its part, in whatever order the owner arranged the blocks.
 */
export const ListingCtx = createContext(null);
const useListing = () => useContext(ListingCtx);

/** The page size a listing template asks for. */
export const pageSizeOf = (settings) => Number(settings && settings.page_size) || PAGE_SIZE;

/**
 * The Studio preview's results: the store's newest listings in the template's sort, with no
 * filters (the preview's controls are inert) and no server facets. Presentation only.
 */
export function previewResult(sf, { f, settings }) {
  const items = sortListings(sf.listings, f.sort).slice(0, pageSizeOf(settings));
  const prices = (sf.listings || []).map((l) => l.price_paise).filter(Number.isFinite);
  const range = prices.length ? { priceMin: Math.floor(Math.min(...prices) / 100 / 1000) * 1000, priceMax: Math.ceil(Math.max(...prices) / 100 / 1000) * 1000 } : {};
  return { items, total: (sf.listings || []).length, facets: null, loading: false, error: '', range };
}

/**
 * Everything the listing slots read, from the storefront, the product type
 * browsed (null: all), the filter state, the template settings and `result`:
 * the server's answer (useCatalogSearch: items, total, facets, all from the
 * query, MERCH-86) or a preview's. `apply` takes the next filter state; the
 * Studio preview passes a no-op.
 */
export function buildListing(sf, { type, f, settings, apply, openFilters, result }) {
  const size = pageSizeOf(settings);
  const r = result || { items: [], total: 0, facets: null, loading: true, error: '' };
  const fc = r.facets ? facetsFrom(r.facets, type, sf.shop.categories) : emptyFacets(type, r.range);
  const win = pageWindow(r.total, f.page, size, settings.paging);
  // The type's collections (registry `collections`: a laptop's Business, Gaming, MacBooks…).
  const cat = collectionOf(type, f.cat);
  const noun = type ? type.plural.toLowerCase() : sf.shop.noun;
  const clear = () => apply(clearFilters(f, type));
  return {
    type,
    cat,
    f,
    fc,
    settings,
    win,
    items: r.items,
    total: r.total,
    loading: r.loading,
    error: r.error,
    chips: activeChips(f, fc),
    nActive: activeFilterCount(f),
    title: cat ? `${cat.label} ${noun}` : f.q ? `Results for “${f.q}”` : type ? type.plural : 'All products',
    noun,
    panel: <FilterPanel f={f} fc={fc} onChange={apply} onClear={clear} type={type} types={type ? [] : sf.shop.inStock} />,
    apply,
    clear,
    openFilters,
  };
}

function CollectionHeader({ props }) {
  const x = useListing();
  const sf = useStorefront();
  const navigate = useNavigate();
  const { type, cat, f } = x;
  const quick = type ? collectionsOf(type).map((c) => ({ key: c.id, label: c.label, on: f.cat === c.id, go: () => x.apply(setFilter(f, 'cat', f.cat === c.id ? '' : c.id)) })) : sf.shop.inStock.length > 1 ? sf.shop.inStock.map((t) => ({ key: t.key, label: `${t.plural} (${t.count})`, to: shopHref(t) })) : [];
  // The intro line: empty or landing-kit sample text is left out (MERCH-55).
  const description = shownText(cat ? cat.hint : type && type.description);
  return (
    <header className="space-y-3">
      <Breadcrumbs
        items={[
          { label: 'Shop', href: '/shop', onClick: () => navigate('/shop') },
          ...(type ? [{ label: type.plural, href: shopHref(type), onClick: () => navigate(shopHref(type)) }] : []),
        ]}
        onHomeClick={() => navigate('/')}
      />
      <div className="min-w-0">
        <h1 className="text-2xl font-bold sm:text-3xl">{x.title}</h1>
        {props.show_description && description ? <p className="mt-1 max-w-2xl text-sm opacity-75">{description}</p> : null}
      </div>
      {props.show_quick_links && quick.length ? (
        <nav aria-label="Quick links" className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 sm:mx-0 sm:flex-wrap sm:px-0">
          {quick.map((q) =>
            q.to ? (
              <Link key={q.key} to={q.to} className="inline-flex min-h-11 shrink-0 items-center rounded-full border border-[var(--app-border)] px-4 text-sm hover:border-primary-600">{q.label}</Link>
            ) : (
              <Button key={q.key} size="sm" variant={q.on ? 'primary' : 'outline'} aria-pressed={q.on} onClick={q.go} className="shrink-0 rounded-full">{q.label}</Button>
            )
          )}
        </nav>
      ) : null}
    </header>
  );
}

function Toolbar() {
  const x = useListing();
  const sf = useStorefront();
  const { f, settings } = x;
  // Below `lg` every template opens filters from a button; "drawer" does it on desktop too.
  const buttonClass = settings.filters === 'drawer' ? '' : 'lg:hidden';
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm opacity-70" aria-live="polite">{x.loading ? 'Loading catalog…' : `${x.total} ${x.total === 1 && x.type ? x.type.label.toLowerCase() : x.noun}`}</p>
        <div className="flex w-full items-center gap-2 sm:w-auto">
          <span className={buttonClass}>
            <Button variant="outline" leftIcon={<SlidersHorizontal className="h-4 w-4" />} onClick={x.openFilters}>
              Filters{x.nActive ? ` (${x.nActive})` : ''}
            </Button>
          </span>
          <div className="min-w-0 flex-1 sm:w-56 sm:flex-none">
            <Select options={SORTS} value={f.sort} onChange={(v) => x.apply(setFilter(f, 'sort', v))} closeOnSelect placeholder="Sort by" />
          </div>
        </div>
      </div>
      {settings.active_chips && x.chips.length ? (
        <div className="flex flex-wrap items-center gap-2">
          {x.chips.map((c) => (
            <RoundedTag key={`${c.key}-${c.value}`} size="sm" label={c.label} onRemove={() => x.apply(removeChip(f, c))} ariaLabel={`Remove filter ${c.label}`} />
          ))}
          <Button size="xs" variant="ghost" onClick={x.clear}>Clear all</Button>
        </div>
      ) : null}
    </div>
  );
}

/** "Bar above the results": one popover per filter group (desktop; phones use the sheet). */
function Filters() {
  const x = useListing();
  if (x.settings.filters !== 'topbar') return null; // the sidebar sits beside the results; the drawer is a sheet
  return (
    <div className="hidden flex-wrap items-center gap-2 lg:flex" role="group" aria-label="Filters">
      {filterGroups(x.f, x.fc, x.type).map((g) => (
        <Popover
          key={g.key}
          placement="bottom-start"
          title={g.label}
          trigger={
            <Button size="sm" variant={g.active ? 'primary' : 'outline'} rightIcon={<ChevronDown className="h-4 w-4" />}>
              {g.label}
              {g.active ? <span className="ml-1.5"><Badge size="xs" variant="secondary">{g.active}</Badge></span> : null}
            </Button>
          }
        >
          <div className="max-h-[60vh] w-72 overflow-y-auto">
            <FilterPanel f={x.f} fc={x.fc} onChange={x.apply} onClear={x.clear} type={x.type} only={g.key} />
          </div>
        </Popover>
      ))}
      {x.nActive ? <Button size="sm" variant="ghost" onClick={x.clear}>Clear all</Button> : null}
    </div>
  );
}

function Results() {
  const x = useListing();
  const { settings, win } = x;
  const body = (
    <CatalogGate>
      {x.error ? (
        <EmptyState title="Could not load these products">{x.error}</EmptyState>
      ) : x.loading && !x.items.length ? (
        <SkeletonGrid />
      ) : x.total === 0 ? (
        <ZeroResults onClear={x.clear} hasFilters={x.nActive > 0 || !!x.f.q} noun={x.noun} />
      ) : (
        <div className="space-y-4">
          <div className={resultsGrid(settings)}>
            {x.items.map((l) => <ListingCard key={l.id} l={l} layout={settings.columns_mobile === '1' ? 'row' : 'stack'} />)}
          </div>
          {settings.paging === 'load_more' ? (
            win.more ? (
              <div className="flex flex-col items-center gap-2 pt-2">
                <p className="text-xs opacity-70">Showing {win.end} of {x.total}</p>
                <Button variant="outline" onClick={() => x.apply(setFilter(x.f, 'page', win.page + 1))}>Load more</Button>
              </div>
            ) : null
          ) : win.pages > 1 ? (
            <div className="flex justify-center pt-2">
              <Pagination currentPage={win.page} totalPages={win.pages} onPageChange={(p) => x.apply(setFilter(x.f, 'page', p))} maxVisiblePages={5} />
            </div>
          ) : null}
        </div>
      )}
    </CatalogGate>
  );
  if (settings.filters !== 'sidebar') return <section className="min-w-0" aria-label="Results">{body}</section>;
  return (
    <div className="grid gap-6 lg:grid-cols-[260px_minmax(0,1fr)]">
      <aside className="hidden lg:block" aria-label="Filters">
        <Card className="sticky top-36 max-h-[calc(100vh-10rem)] overflow-y-auto p-4">{x.panel}</Card>
      </aside>
      <section className="min-w-0" aria-label="Results">{body}</section>
    </div>
  );
}

/** Zero results: say so, offer to clear, and recommend in-stock alternatives (WEB-02). */
function ZeroResults({ onClear, hasFilters, noun }) {
  const sf = useStorefront();
  const picks = sortListings(sf.listings, 'newest').slice(0, 3);
  return (
    <div className="space-y-4">
      <EmptyState title={`No ${noun} match these filters`} action={hasFilters ? <Button onClick={onClear}>Clear filters</Button> : null}>
        Try fewer filters or a wider price range. New units are inspected and listed every week.
      </EmptyState>
      {picks.length ? (
        <div className="space-y-3">
          <h2 className="font-semibold">You might like</h2>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {picks.map((l) => <ListingCard key={l.id} l={l} />)}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** Slot type → component (a registry: a new slot is one entry here plus its landing-kit declaration). */
export const LISTING_SLOT_COMPONENTS = {
  collection_header: CollectionHeader,
  toolbar: Toolbar,
  filters: Filters,
  results: Results,
};
